import * as XLSX from 'xlsx';
import { useEffect, useState } from 'react';
import { SECTORS, getCatalog } from './catalog';
import { download } from './exporters';

const KEY = 'giss_mfr';
const SEEN = 'giss_mfr_seen';
const API = `${process.env.REACT_APP_BACKEND_URL}/api/mfr`;
const ev = () => window.dispatchEvent(new Event('giss-mfr'));

export const getMfrs = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };
const saveMfrs = (m) => { localStorage.setItem(KEY, JSON.stringify(m)); ev(); };
export const getMfr = (id) => getMfrs()[id] || null;
export const deleteMfr = (id) => { const m = getMfrs(); delete m[id]; saveMfrs(m); };

export function useMfrs() {
  const [m, setM] = useState(getMfrs);
  useEffect(() => { const on = () => setM(getMfrs()); window.addEventListener('giss-mfr', on); return () => window.removeEventListener('giss-mfr', on); }, []);
  return m;
}

// keep own prices / article numbers when a catalogue is refreshed
function merge(doc, old) {
  const prev = Object.fromEntries((old?.items || []).map((x) => [x.id, x]));
  return { ...doc, items: doc.items.map((x) => (prev[x.id] ? { ...x, price: prev[x.id].price ?? x.price, install: prev[x.id].install ?? x.install, sku: prev[x.id].sku || x.sku } : x)) };
}
export const safeUrl = (u) => (/^https?:\/\//i.test(String(u || '').trim()) ? String(u).trim() : '');
const clean = (doc) => ({ ...doc, url: safeUrl(doc.url), items: doc.items.map((x) => ({ ...x, url: safeUrl(x.url), pdf: safeUrl(x.pdf), img: safeUrl(x.img) })) });
export const putMfr = (raw, keep = true) => { const doc = clean(raw), m = getMfrs(); m[doc.id] = keep ? merge(doc, m[doc.id]) : doc; saveMfrs(m); return m[doc.id]; };
export const setMfrItem = (mid, iid, patch) => { const m = getMfrs(); m[mid].items = m[mid].items.map((x) => (x.id === iid ? { ...x, ...patch } : x)); saveMfrs(m); };

export async function fetchRegistry() {
  const r = await fetch(API);
  if (!r.ok) throw new Error('offline');
  return (await r.json()).items;
}
export async function downloadMfr(id) {
  const r = await fetch(`${API}/${id}`);
  if (!r.ok) throw new Error('offline');
  return putMfr({ ...(await r.json()), loadedAt: Date.now() });
}
export async function publishMfr(doc, token) {
  const { loadedAt, ...body } = doc;
  const r = await fetch(API, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Owner-Token': token || '' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error('publish');
  return r.json();
}
export const getSeen = () => { try { return JSON.parse(localStorage.getItem(SEEN)) || []; } catch { return []; } };
export const markSeen = (ids) => localStorage.setItem(SEEN, JSON.stringify([...new Set([...getSeen(), ...ids])]));

// all manufacturer items (flat) with manufacturer name
export const mfrItems = (m = getMfrs()) => Object.values(m).flatMap((d) => d.items.map((x) => ({ ...x, mfrId: d.id, mfr: d.name })));
export function searchMfr(list, q, sector) {
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return list.filter((x) => (!sector || sector === 'all' || !x.sector || x.sector === sector) && words.every((w) => `${x.mfr} ${x.name} ${x.dim} ${x.mat} ${x.sku} ${x.pn}`.toLowerCase().includes(w)));
}

// ---- Excel templates per sector ----
const COLS = ['mfColMfr', 'mfColSku', 'mfColName', 'mfColCat', 'mfColDn', 'mfColPn', 'mfColDim', 'mfColMat', 'mfColUnit', 'mfColPrice', 'mfColInstall', 'mfColDesc', 'mfColLink'];
const row = (x, mfr) => [mfr, x.sku || '', x.name, x.cat || '', x.dn ?? '', x.pn || '', x.dim || '', x.mat || '', x.unit || 'pcs', x.price ?? '', x.install ?? '', x.desc || '', x.url || ''];
function sheetFor(t, sec) {
  const own = getCatalog().items.filter((x) => (x.sector || 'water') === sec || (!x.sector && sec === 'water' && x.id?.[0] === 'w'));
  const mf = mfrItems().filter((x) => (x.sector || 'water') === sec);
  const rows = [COLS.map((k) => t[k]), ...own.map((x) => row(x, t.rmCatOwn)), ...mf.map((x) => row(x, x.mfr))];
  for (let i = 0; i < 20; i++) rows.push([]);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [22, 14, 44, 12, 7, 10, 22, 18, 8, 13, 13, 50, 40].map((w) => ({ wch: w }));
  return ws;
}
export function exportTemplate(t, sec) {
  const wb = XLSX.utils.book_new();
  (sec === 'all' ? SECTORS : [sec]).forEach((s) => XLSX.utils.book_append_sheet(wb, sheetFor(t, s), String(t['sec_' + s] || s).replace(/[\\/?*[\]:]/g, ' ').slice(0, 31)));
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  download(new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `GISS-Materialkatalog_${sec === 'all' ? 'alle-Sparten' : sec}.xlsx`);
}
const slug = (s) => String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 36) || 'hersteller';
const num = (v) => { if (v === '' || v == null) return null; const n = +String(v).replace(/\s/g, '').replace(',', '.'); return Number.isFinite(n) ? n : null; };
export async function importTemplate(t, file) {
  const wb = XLSX.read(await file.arrayBuffer());
  const secByName = Object.fromEntries(SECTORS.map((s) => [String(t['sec_' + s] || s).replace(/[\\/?*[\]:]/g, ' ').slice(0, 31), s]));
  const groups = {};
  wb.SheetNames.forEach((sn) => {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[sn], { header: 1, defval: '' });
    rows.slice(1).forEach((r, i) => {
      const [mfr, sku, name, cat, dn, pn, dim, mat, unit, price, install, desc, url] = r;
      if (!String(name).trim() || String(mfr).trim() === t.rmCatOwn) return;
      const m = String(mfr).trim() || t.mfOwn, id = `own-${slug(m)}`;
      const g = groups[id] || (groups[id] = { id, name: m, url: '', note: file.name, version: new Date().toISOString().slice(0, 10), items: [] });
      g.items.push({ id: `${id}-${slug(sku || name)}-${slug(dim || dn)}-${i}`.slice(0, 90), name: String(name).trim(), family: String(name).trim(), cat: String(cat).trim() || 'valve', sector: secByName[sn] || 'water', dn: num(dn), pn: String(pn), dim: String(dim || (dn ? `DN ${dn}` : '')), mat: String(mat), unit: String(unit).trim() || 'pcs', price: num(price), install: num(install), sku: String(sku), desc: String(desc), url: String(url), img: '', pdf: '' });
    });
  });
  const docs = Object.values(groups);
  if (!docs.length) throw new Error('empty');
  // rows of an already downloaded manufacturer only update its prices / article numbers
  const have = Object.values(getMfrs());
  docs.forEach((g) => {
    const d = have.find((x) => x.name.trim().toLowerCase() === g.name.toLowerCase());
    if (!d) return putMfr(g);
    const key = (x) => `${x.name}|${x.dim}`.toLowerCase();
    const upd = Object.fromEntries(g.items.map((x) => [key(x), x]));
    const extra = g.items.filter((x) => !d.items.some((y) => key(y) === key(x)));
    return putMfr({ ...d, items: [...d.items.map((y) => (upd[key(y)] ? { ...y, price: upd[key(y)].price, install: upd[key(y)].install, sku: upd[key(y)].sku || y.sku } : y)), ...extra] }, false);
  });
  return { n: docs.reduce((s, d) => s + d.items.length, 0), m: docs.length };
}

// ---- matching manufacturer products for a catalogue item (same kind & design) ----
const FORMS = [
  ['gardenhyd', /gartenhydrant|garden hydrant/i], ['uf', /unterflur|underground hydrant|\bufh\b/i], ['ue', /überflur|ueberflur|above.?ground hydrant|\büfh\b/i],
  ['standpipe', /standrohr|standpipe/i], ['bev', /entlüft|bev\b|air valve|airlift/i], ['capair', /straßenkappe.*lüftung/i], ['cap', /straßenkappe|street cap|surface box/i],
  ['tiptop', /einbaugarnitur|schutzrohr|teleskopschutz/i], ['meter', /wasserzähler|water meter|\bwz\b/i], ['backflow', /rückstau|backwater|backflow flap/i],
  ['rv', /rückfluss|rückschlag|check valve|non.?return/i], ['pas', /passstück|ausbaustück|flanschadapter|dismantling/i], ['cover', /abdeckung|cover/i],
  ['shaft', /schacht|chamber|manhole/i], ['tee', /t-stück|\btee\b/i], ['bend', /bogen|\bbend\b/i], ['flush', /spül|flush/i], ['saug', /saugstelle|saugkorb|suction/i],
];
export const formOf = (name) => (FORMS.find(([, re]) => re.test(String(name || ''))) || [null])[0];
const PIPE_MATS = [['gfk', /gfk|grp|prfv|prv/i], ['inox', /edelstahl|stainless|inox|1\.4571|1\.4404/i], ['ggg', /guss|ggg|ductile|fonte|ghisa|sml/i], ['pvc', /pvc/i], ['pe', /\bpe\b|pe-hd|pe ?100|pe-x|pe-rc/i], ['pp', /\bpp\b/i], ['stz', /steinzeug|stz|vitrified|grès|gres/i], ['beton', /beton|concrete|béton|calcestruzzo/i], ['fz', /faserzement|fibre cement|amianto|fibrocemento/i], ['st', /stahl|steel|acier|acciaio/i]];
const pipeMat = (x) => (PIPE_MATS.find(([, re]) => re.test(`${x?.name || ''} ${x?.dim || ''} ${x?.mat || ''}`)) || [null])[0];
const isPipeProd = (x) => x.unit === 'lfm' || x.cat === 'pipe';
export function matchProducts(item, m = getMfrs()) {
  if (item?.kind === 'pipe') {
    const pm = pipeMat(item);
    return pm ? mfrItems(m).filter((x) => isPipeProd(x) && pipeMat(x) === pm).sort((a, b) => a.mfr.localeCompare(b.mfr) || (a.dn || 0) - (b.dn || 0)) : [];
  }
  const f = formOf(item?.name);
  if (!f) return [];
  return mfrItems(m).filter((x) => !isPipeProd(x) && formOf(x.name) === f).sort((a, b) => a.mfr.localeCompare(b.mfr) || a.name.localeCompare(b.name) || (a.dn || 0) - (b.dn || 0));
}
export const prodOf = (x) => ({ mfrId: x.mfrId, mfr: x.mfr, ref: x.id, name: x.name, dim: x.dim || '', sku: x.sku || '', mat: x.mat || '', url: x.url || '', pdf: x.pdf || '', img: x.img || '', price: x.price ?? null });
const SHOW = 'giss_mfr_show';
export const showProducts = () => localStorage.getItem(SHOW) !== '0';
export const setShowProducts = (v) => localStorage.setItem(SHOW, v ? '1' : '0');
